import { SENHA_MAXIMO, SENHA_MINIMO } from "@base/contracts";
import {
  Button,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  Input,
} from "@base/ui";
import { useState, type FormEvent } from "react";
import { Link } from "../../../shared/navegacao/navegacao";
import { useRedefinirSenha } from "../application/use-usuarios";
import { ArrowRight } from "lucide-react";
import {
  BOTAO_DE_ACESSO,
  CAMPO_DE_ACESSO,
  CabecalhoDeAcesso,
  LayoutDeAcesso,
} from "./layout-de-acesso";
import { mensagemDoErro } from "../../../shared/http/mensagem-de-erro";

const TEXTOS = {
  convite: {
    rotulo: "Primeiro acesso",
    titulo: "Crie a sua senha",
    descricao: "Este é o seu primeiro acesso ao painel do cliente Monte Saúde.",
    botao: "Criar senha",
  },
  redefinicao: {
    rotulo: "Recuperar acesso",
    titulo: "Crie uma senha nova",
    descricao: "Depois de salvar, as sessões abertas em outros aparelhos são encerradas.",
    botao: "Salvar senha nova",
  },
} as const;

/**
 * Definicao de senha pelo link do e-mail, para convite e redefinicao.
 *
 * O token e lido uma vez e tirado da barra de endereco: assim ele nao fica no
 * historico do navegador nem aparece num print da tela.
 */
export function PaginaDefinirSenha({
  token,
  finalidade,
  aoConcluir,
}: {
  readonly token: string | null;
  readonly finalidade: "convite" | "redefinicao";
  readonly aoConcluir: () => void;
}) {
  const redefinir = useRedefinirSenha();
  const [senha, setSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [tentouEnviar, setTentouEnviar] = useState(false);
  const textos = TEXTOS[finalidade];

  const curta = senha.length < SENHA_MINIMO;
  const diferente = confirmacao !== senha;

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    setTentouEnviar(true);
    if (!token || curta || diferente) return;
    redefinir.mutate({ token, novaSenha: senha }, { onSuccess: aoConcluir });
  }

  if (!token) {
    return (
      <LayoutDeAcesso>
        <div role="alert" className="flex flex-col gap-6">
          <CabecalhoDeAcesso
            rotulo="Recuperar acesso"
            titulo="Link incompleto"
            descricao="Abra o link exatamente como chegou no e-mail, ou peça um novo."
          />
          <Link para="/esqueci-senha" className="text-xs font-bold underline underline-offset-4">
            Pedir um link novo
          </Link>
        </div>
      </LayoutDeAcesso>
    );
  }

  return (
    <LayoutDeAcesso>
      <form className="flex flex-col gap-6" onSubmit={enviar} noValidate>
        <FieldGroup>
          <CabecalhoDeAcesso
            rotulo={textos.rotulo}
            titulo={textos.titulo}
            descricao={textos.descricao}
          />
          <Field data-invalid={(tentouEnviar && curta) || undefined}>
            <FieldLabel htmlFor="senha" className="text-xs font-bold">Senha</FieldLabel>
            <Input
              id="senha"
              type="password"
              autoComplete="new-password"
              maxLength={SENHA_MAXIMO}
              value={senha}
              onChange={(evento) => setSenha(evento.target.value)}
              aria-describedby="regra-da-senha"
              className={CAMPO_DE_ACESSO}
              required
            />
            <FieldDescription id="regra-da-senha" className="text-xs">
              Mínimo de {SENHA_MINIMO} caracteres. Uma frase curta é mais fácil de lembrar e
              mais difícil de adivinhar.
            </FieldDescription>
          </Field>
          <Field data-invalid={(tentouEnviar && diferente) || undefined}>
            <FieldLabel htmlFor="confirmacao" className="text-xs font-bold">Confirme a senha</FieldLabel>
            <Input
              id="confirmacao"
              type="password"
              autoComplete="new-password"
              maxLength={SENHA_MAXIMO}
              value={confirmacao}
              onChange={(evento) => setConfirmacao(evento.target.value)}
              className={CAMPO_DE_ACESSO}
              required
            />
          </Field>
          {tentouEnviar && curta ? (
            <FieldError>A senha precisa ter pelo menos {SENHA_MINIMO} caracteres.</FieldError>
          ) : tentouEnviar && diferente ? (
            <FieldError>As duas senhas não são iguais.</FieldError>
          ) : redefinir.isError ? (
            <FieldError>{mensagemDoErro(redefinir.error)}</FieldError>
          ) : null}
          <Field>
            <Button type="submit" className={BOTAO_DE_ACESSO} disabled={redefinir.isPending}>
              {redefinir.isPending ? "Salvando…" : textos.botao}
              <ArrowRight aria-hidden="true" />
            </Button>
          </Field>
        </FieldGroup>
      </form>
    </LayoutDeAcesso>
  );
}
